/**
 * /feed/:id — детальна сторінка однієї вакансії.
 *
 * UX:
 *   BackNav (to /feed)
 *   Hero: категорія + час
 *   AI-summary (italic preview)
 *   Повний текст вакансії
 *   Contacts block:
 *     - PRO / вже відкривав сьогодні → clickable @user, phone, urls
 *     - FREE заблокований → blur-overlay з кнопкою «Відкрити (N/5)»
 *   Rating client: ★ ★ ★ ★ ★ tap-to-vote
 *   Reviews expandable
 */

import { useEffect, useRef, useState } from "react";
import { filterContactLinks } from "@/lib/contactLinks";
import PostLinkRow from "@/components/PostLinkRow";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { BackNav } from "@/components/BackNav";
import { FactPills } from "@/components/FactPills";
import { WorkKindBadge } from "@/components/WorkKindBadge";
import { Icon } from "@/components/Icon";
import { VacancyMediaBlock } from "@/components/VacancyMediaBlock";
import { LockedSectionPopup } from "@/components/LockedSectionPopup";
import { isActiveSubscription } from "@/api/types";
import { useContactLimitPopup } from "@/components/SubscriptionUpsell";
import { TgEmoji } from "@/components/TgEmoji";
import { RemovedVacancyNotice } from "@/components/RemovedVacancyNotice";
import { renderVacancyHtml, extractFooter } from "@/lib/vacancy_text";
import { openExternalOnAnchorClick, openTelegramUserLink } from "@/lib/openExternal";
import { markVacancyViewed } from "@/lib/viewedVacancies";
import { showAppAlert } from "@/lib/appAlert";
import { showReviewSaved } from "@/lib/reviewReward";
import { AIHelperStub } from "./Feed";

const SOURCE_LABEL: Record<string, string> = {
  telegram_channel: "TG-канал",
  telegram_chat: "TG-чат",
  threads: "Threads",
  freelancehunt: "Freelancehunt",
  finder: "Finder",
  user_submitted: "Користувач",
};

// Бейдж формату роботи (owner 2026-07-23). unspecified → без бейджа.
const WORK_FORMAT_BADGE: Record<string, { label: string; cls: string }> = {
  online: { label: "Онлайн", cls: "text-emerald-700 bg-emerald-100" },
  offline: { label: "Офлайн", cls: "text-blue-700 bg-blue-100" },
  hybrid: { label: "Гібрид", cls: "text-purple-700 bg-purple-100" },
};
import { CenteredError, Spinner } from "@/components/Spinner";
import {
  useDeleteReview,
  useFeedVacancy,
  useFlagNotVacancy,
  useHideVacancy,
  useAdminRemoveVacancy,
  useMe,
  useSubmitReview,
  useUnlockContacts,
  useToggleSaveVacancy,
  useMySubscription,
  useVacancyReviews,
} from "@/api/hooks";
import { showToast } from "@/lib/toast";
import type { ParsedContacts } from "@/api/hooks";
import { tg } from "@/lib/telegram";

function relTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return "щойно";
  if (diff < 3600) return `${Math.floor(diff / 60)} хв тому`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} год тому`;
  return d.toLocaleString("uk-UA", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Делегуємо в openTelegramUserLink: ПК/веб → openLink (на десктопі
// openTelegramLink закривав апку без резолву — юзер підтвердив 2026-06-11).
function openTgLink(url: string) {
  openTelegramUserLink(url);
}

type UrlClassification = {
  label: string;
  /** Один з імен в Icon.tsx — використовуємо line-art / brand icon замість emoji */
  icon: import("@/components/Icon").IconName;
  /** Колір кружка під іконку (Tailwind bg-* + text-*) */
  tone: "primary" | "emerald" | "blue" | "pink" | "purple" | "ink";
};

function classifyUrl(url: string): UrlClassification {
  const u = url.toLowerCase();
  if (u.includes("wa.me/") || u.includes("whatsapp.com"))
    return { label: "WhatsApp", icon: "whatsapp", tone: "emerald" };
  if (u.startsWith("viber://") || u.includes("viber"))
    return { label: "Viber", icon: "chat-call", tone: "purple" };
  // Threads — конкретний контекст у Threads-вакансії: посилання на оригінал.
  if (u.includes("threads.net") || u.includes("threads.com"))
    return { label: "Посилання на вакансію", icon: "threads", tone: "ink" };
  if (u.includes("instagram.com"))
    return { label: "Інстаграм замовника", icon: "instagram", tone: "pink" };
  // Юзер 2026-05-23: «у вакансіях з Freelancehunt замість "Посилання"
  // має бути "Посилання на вакансію"» — окрема гілка ПЕРЕД generic fallback.
  if (u.includes("freelancehunt.com"))
    return { label: "Посилання на вакансію", icon: "project", tone: "primary" };
  if (u.includes("facebook.com") || u.includes("fb.com"))
    return { label: "Facebook", icon: "facebook", tone: "blue" };
  if (u.includes("linkedin.com"))
    return { label: "LinkedIn", icon: "linkedin", tone: "blue" };
  if (u.includes("docs.google.com") || u.includes("forms.gle"))
    return { label: "Google Forms", icon: "doc", tone: "primary" };
  if (
    u.includes("tally.so") ||
    u.includes("typeform.com") ||
    u.includes("notion.so") ||
    u.includes("airtable.com")
  ) {
    return { label: "Форма", icon: "form", tone: "primary" };
  }
  return { label: "Посилання", icon: "globe", tone: "primary" };
}

/** Map tone → Tailwind class для bg + text icon-circle. */
function toneClasses(tone: UrlClassification["tone"]): {
  bg: string;
  text: string;
} {
  switch (tone) {
    case "emerald":
      return { bg: "bg-emerald-100", text: "text-emerald-700" };
    case "blue":
      return { bg: "bg-blue-100", text: "text-blue-700" };
    case "pink":
      return { bg: "bg-pink-100", text: "text-pink-600" };
    case "purple":
      return { bg: "bg-purple-100", text: "text-purple-700" };
    case "ink":
      return { bg: "bg-ink/10", text: "text-ink" };
    case "primary":
    default:
      return { bg: "bg-primary/10", text: "text-primary" };
  }
}

/**
 * ContactsList — redesigned 2026-05-22 (юзер «оформи інакше, виглядає
 * не дуже»).
 *
 * Принципи:
 * 1. Primary CTA — перший Telegram-контакт як ВЕЛИКА оранжева кнопка
 *    «Написати в Telegram» (one-tap дія, фрілансер пише замовнику).
 * 2. Решта контактів — компактні рядки з icon-circle.
 * 3. URL'и (Threads/Instagram/etc) — горизонтальні chip'и в Grid 2-col,
 *    щоб не з'їдати вертикальний простір.
 * 4. Без `bg-primary/8` тла — мінімалістично, чисто.
 */
/**
 * ContactsList — 1:1 з Feed.tsx inline-expand (юзер 2026-05-23: «мені
 * подобається оранджева кнопка і її стиль — оформлення має бути ОДНАКОВЕ
 * в стрічці і на сторінці вакансії»).
 *
 *   TG primary: ORANGE CTA з Telegram icon, Name (білий) + @username (білий/dim)
 *   Інші TG: компактні з border
 *   Phone: emerald icon-circle / Email: blue icon-circle
 *   URLs (Threads/IG/etc): brand-icon-circle з label + URL знизу
 */
function ContactsList({
  contacts,
  postUrl,
}: {
  contacts: ParsedContacts;
  postUrl?: string | null;
}) {
  // Юзернейм може прийти і з «@», і без — залежить від джерела. Малюємо
  // «собачку» самі, тому чужу спершу знімаємо, інакше виходить «@@nick»
  // (owner 2026-09-08).
  const strip = (u: string) => u.replace(/^@+/, "");
  const [primaryTg, ...secondaryTg] = contacts.telegrams.map(strip);
  // Юзер 2026-05-23: «вакансія де двічі юзернейм написаний» — раніше
  // fallback `?? @${primaryTg}` робив, що великий рядок і малий рядок
  // ставали ОДНАКОВИМИ. Тепер: якщо реального Name немає → показуємо
  // ОДИН рядок (@username великий), без duplicated subtitle.
  const primaryName = primaryTg ? contacts.tg_names?.[primaryTg] ?? null : null;

  return (
    <div className="space-y-3">
      {/* Primary TG — ORANGE CTA */}
      {primaryTg && (
        <button
          type="button"
          onClick={() => openTgLink(`https://t.me/${primaryTg}`)}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-primary text-white shadow-card active:bg-primary-deep transition"
        >
          <div className="grid h-9 w-9 place-items-center rounded-full bg-white/20 text-white shrink-0">
            <Icon name="telegram" size={16} color="#FFFFFF" />
          </div>
          <div className="flex-1 min-w-0 text-left">
            {primaryName ? (
              <>
                <div className="font-display text-[0.875rem] font-bold text-white truncate">
                  {primaryName}
                </div>
                <div className="font-mono text-[0.75rem] text-white/80 mt-0.5 truncate">
                  @{primaryTg}
                </div>
              </>
            ) : (
              <div className="font-display text-[0.875rem] font-bold text-white truncate">
                @{primaryTg}
              </div>
            )}
          </div>
          <Icon name="arrow-right" size={16} color="#FFFFFF" className="-rotate-45" />
        </button>
      )}


      {/* Secondary TG — компактні з border */}
      {secondaryTg.length > 0 && (
        <div className="space-y-1.5">
          {secondaryTg.map((u) => {
            const name = contacts.tg_names?.[u];
            return (
              <button
                key={u}
                type="button"
                onClick={() => openTgLink(`https://t.me/${u}`)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-ink/[0.08] active:bg-surface-soft transition text-left"
              >
                <div className="grid h-7 w-7 place-items-center rounded-full bg-primary/10 text-primary shrink-0">
                  <Icon name="telegram" size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  {name && (
                    <div className="font-display text-[0.78125rem] font-semibold text-ink truncate">
                      {name}
                    </div>
                  )}
                  <div className="font-mono text-[0.71875rem] text-ink-dim truncate">
                    @{u}
                  </div>
                </div>
                <Icon name="arrow-right" size={12} color="rgba(254,107,4,0.85)" className="-rotate-45" />
              </button>
            );
          })}
        </div>
      )}

      {/* Phone — emerald icon + label «Телефон» / number */}
      {contacts.phones.map((p) => (
        <a
          key={p}
          href={`tel:${p}`}
          className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl border border-ink/[0.08] active:bg-surface-soft transition"
        >
          <div className="grid h-9 w-9 place-items-center rounded-full bg-emerald-100 text-emerald-700 shrink-0">
            <Icon name="phone" size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-[0.75rem] font-semibold uppercase tracking-[0.06em] text-emerald-700">
              Телефон
            </div>
            <div className="font-mono text-[0.8125rem] text-ink mt-0.5 truncate">
              {p}
            </div>
          </div>
          <Icon name="arrow-right" size={14} color="rgba(5,150,105,0.75)" className="-rotate-45" />
        </a>
      ))}

      {/* Email — blue icon + label «Email» / address */}
      {contacts.emails.map((e) => (
        <a
          key={e}
          href={`mailto:${e}`}
          className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl border border-ink/[0.08] active:bg-surface-soft transition"
        >
          <div className="grid h-9 w-9 place-items-center rounded-full bg-blue-100 text-blue-700 shrink-0">
            <Icon name="mail" size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-[0.75rem] font-semibold uppercase tracking-[0.06em] text-blue-700">
              Email
            </div>
            <div className="font-mono text-[0.8125rem] text-ink mt-0.5 truncate">
              {e}
            </div>
          </div>
          <Icon name="arrow-right" size={14} color="rgba(29,78,216,0.75)" className="-rotate-45" />
        </a>
      ))}

      {/* URL'и — двохрядковий: лейбл (з classifyUrl) + сам URL знизу.
          Threads → «Посилання на вакансію» + https://threads.com/...
          Instagram → «Інстаграм замовника» + https://instagram.com/...
          Freelancehunt → «Посилання на вакансію» (через generic globe icon).
      */}
      {contacts.urls.map((u) => {
        const c = classifyUrl(u);
        const tone = toneClasses(c.tone);
        // Display URL без `https://` префікса — економимо місце,
        // обриваємо trailing slash.
        const displayUrl = u
          .replace(/^https?:\/\//, "")
          .replace(/\/$/, "");
        return (
          <button
            key={u}
            type="button"
            onClick={() => openTgLink(u)}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl border border-ink/[0.08] active:bg-surface-soft transition text-left"
          >
            <div
              className={
                "grid h-9 w-9 place-items-center rounded-full shrink-0 " +
                tone.bg +
                " " +
                tone.text
              }
            >
              <Icon name={c.icon} size={16} />
            </div>
            <div className="flex-1 min-w-0">
              {/* Юзер 2026-05-23: «Посилання на вакансію» / «Інстаграм
                  замовника» — не дозволяти переносити на 2 рядки на
                  жодному пристрої. whitespace-nowrap + truncate +
                  трохи менший font/tracking. */}
              <div className="font-display text-[0.6875rem] font-semibold uppercase tracking-[0.04em] text-ink whitespace-nowrap truncate">
                {c.label}
              </div>
              <div className="font-mono text-[0.71875rem] text-ink-dim mt-0.5 truncate">
                {displayUrl}
              </div>
            </div>
            <Icon name="arrow-right"
              size={14}
              color="rgba(254,107,4,0.85)" className="-rotate-45" />
          </button>
        );
      })}
      {postUrl && <PostLinkRow url={postUrl} />}
    </div>
  );
}

function BlurredContacts({
  attemptsLeft,
  onUnlock,
  onShowLimit,
  isUnlocking,
}: {
  attemptsLeft: number;
  onUnlock: () => void;
  onShowLimit: () => void;
  isUnlocking: boolean;
}) {
  const total = 5;  // owner 2026-07-09: ліміт 2/день → 5/тиждень
  const limitReached = attemptsLeft === 0;
  return (
    // Висота блоку — щоб кнопка не впиралась у краї (owner 2026-09-12, двічі:
    // «оно немного выпирает», «сделай блок выше или кнопки уменьшай»). Було
    // 10.625rem і текст+кнопка займали майже всю висоту; тепер 12.75rem,
    // плюс нижче зменшена кнопка й додано вертикальний внутрішній відступ.
    <div className="relative rounded-2xl overflow-hidden border border-primary/15 bg-gradient-to-br from-primary/[0.04] to-primary/[0.02] min-h-[12.75rem]">
      {/* Blurred placeholder rows — імітують справжній список */}
      <div
        className="p-3 space-y-1.5 pointer-events-none select-none"
        style={{ filter: "blur(7px)", opacity: 0.55 }}
      >
        {/* Завжди 3 placeholders щоб overlay не скукоживався */}
        {Array.from({ length: 3 }).map(
          (_, i) => (
            <div
              key={i}
              className="h-9 rounded-xl bg-ink/[0.07]"
              style={{ width: `${85 - i * 12}%` }}
            />
          ),
        )}
      </div>
      {/* Foreground CTA — over the blur */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-5 py-4 bg-gradient-to-t from-surface via-surface/95 to-surface/70">
        <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10 mb-2.5">
          <Icon name="lock" size={20} color="#FE6B04" />
        </div>
        <div className="font-display text-[0.875rem] font-bold text-ink mb-1">
          Контакти приховано
        </div>
        {/* Рядки однакової довжини (owner 2026-09-12: «на другий рядок
            перенеслось лише слово тиждень»). */}
        <div className="text-[0.71875rem] text-ink-dim mb-3.5 leading-snug max-w-[13.5rem] [text-wrap:balance]">
          {limitReached
            ? "Безкоштовний ліміт 5/5 вичерпано на цей тиждень"
            : "Відкрий, щоб написати замовнику напряму"}
        </div>
        <button
          type="button"
          onClick={limitReached ? onShowLimit : onUnlock}
          disabled={isUnlocking}
          className={
            // Кнопка МЕНША за попередню (owner 2026-09-12): px-3.5/py-2 і
            // кегль 0.75rem замість px-5/py-2.5 і 0.8125rem.
            "inline-flex max-w-full items-center justify-center gap-1.5 rounded-full px-3.5 py-2 font-display text-[0.75rem] font-bold transition whitespace-nowrap shadow-card-soft " +
            (isUnlocking
              ? "bg-ink/10 text-ink-dim cursor-not-allowed"
              : limitReached
                ? "bg-dark text-white active:bg-ink"
                : "bg-primary text-white active:bg-primary-deep")
          }
        >
          {isUnlocking ? (
            "Відкриваю..."
          ) : limitReached ? (
            <>
              <TgEmoji char="🔒" size={14} />
              Зняти ліміт підпискою
            </>
          ) : (
            <>
              <TgEmoji char="🔑" size={14} />
              Відкрити · {attemptsLeft}/{total}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function StarRow({
  value,
  onPick,
  disabled,
}: {
  value: number;
  onPick?: (n: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={disabled || !onPick}
          onClick={() => onPick?.(i)}
          className={
            "transition text-[1.5rem] leading-none " +
            (i <= value ? "text-primary" : "text-ink/20") +
            (onPick && !disabled ? " active:scale-90" : " cursor-default")
          }
        >
          ★
        </button>
      ))}
    </div>
  );
}

function ReviewsSection({
  vacancyId,
  contactUsername,
  unlocked,
  clientRatingAvg,
  clientRatingCount,
  clientReviewsCount,
}: {
  vacancyId: number;
  contactUsername: string;
  unlocked: boolean;
  /** Юзер 2026-05-23: «одна вакансія в feed inline і detail page
   * має МАТИ ОДНАКОВІ дані». Передаємо rating з API → показуємо
   * у заголовку Reviews-блоку 1:1 з Feed.tsx. */
  clientRatingAvg: number | null;
  clientRatingCount: number;
  /** Юзер 2026-05-23: «додай третю підпис — кількість відгуків (ноль
   *  відгуків / один відгук / два відгуки...)». Не чекаємо useVacancyReviews —
   *  count приходить з API одразу. */
  clientReviewsCount: number;
}) {
  const [open, setOpen] = useState(false);
  const reviewsQ = useVacancyReviews(vacancyId, open);

  // Українська плюралізація (1 / 2-4 / 5+)
  const votesWord =
    clientRatingCount === 1
      ? "голос"
      : clientRatingCount >= 2 && clientRatingCount <= 4
        ? "голоси"
        : "голосів";
  const reviewsWord =
    clientReviewsCount === 1
      ? "відгук"
      : clientReviewsCount >= 2 && clientReviewsCount <= 4
        ? "відгуки"
        : "відгуків";
  // Збираємо завжди-on label'и: ★ + votes-якщо-є + reviews-ЗАВЖДИ.
  const statusParts: string[] = [];
  if (clientRatingCount > 0) {
    statusParts.push(`★ ${clientRatingAvg != null ? clientRatingAvg.toFixed(2).replace(/0$/, "") : "—"}`);
    statusParts.push(`${clientRatingCount} ${votesWord}`);
  }
  statusParts.push(`${clientReviewsCount} ${reviewsWord}`);
  const statusLine = statusParts.join(" · ");
  // Надійний замовник: ≥10 оцінок і avg ≥ 4.8 (юзер 2026-06-13).
  const reliable = clientRatingCount >= 10 && (clientRatingAvg ?? 0) >= 4.8;

  return (
    <div className="rounded-ios-card bg-surface shadow-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-surface-soft transition"
      >
        <div className="grid h-10 w-10 place-items-center rounded-[0.75rem] bg-primary/10 text-primary">
          <Icon name="chat" size={16} />
        </div>
        <div className="flex-1 min-w-0">
          {reliable && (
            <div className="-ml-2 mb-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[0.625rem] font-display font-semibold text-emerald-600">
              <TgEmoji char="✅" size={13} />
              Надійний замовник
            </div>
          )}
          <div className="font-display text-[0.875rem] font-semibold text-ink truncate">
            {unlocked ? `Відгуки про @${contactUsername}` : "Відгуки про клієнта"}
          </div>
          <div className="mt-0.5 text-[0.6875rem] text-ink-dim truncate">
            {statusLine}
          </div>
        </div>
        <Icon name="chevron" size={14} color="rgba(23,20,17,0.45)" className={"text-ink-dim transition-transform duration-200 " + (open ? "rotate-180" : "")} />
      </button>

      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4">
            {reviewsQ.isLoading && <Spinner />}
            {/* Відгук = ТІЛЬКИ текст (юзер 2026-06-11). Оцінка-зірки без
                коментаря НЕ показується у списку (і не рахується — backend
                client_reviews_count теж лише текстові). Фільтруємо. */}
            {(() => {
              const textReviews =
                reviewsQ.data?.items.filter(
                  (r) => r.review_text && r.review_text.trim(),
                ) ?? [];
              if (reviewsQ.data && textReviews.length === 0) {
                return (
                  <div className="text-[0.75rem] text-ink-dim text-center py-2">
                    Відгуків ще немає. Будь першим хто залишить.
                  </div>
                );
              }
              return textReviews.map((r) => (
                <div
                  key={r.id}
                  /* Кожен відгук — окрема плитка (owner 2026-09-17: «відгуки
                     ніяк одне від одного не відділені»). Волосинки на 6%
                     людина не бачила. */
                  className="mb-2 rounded-xl border border-ink/[0.07] bg-bg/70 p-3 last:mb-0"
                >
                  <div className="flex items-center justify-between mb-1">
                    <StarRow value={r.rating} />
                    {/* Відгуки анонімні (owner 2026-08-24). */}
                    <span className="font-mono text-[0.625rem] text-ink-dim">
                      Анонімно
                    </span>
                  </div>
                  <p className="text-[0.8125rem] leading-relaxed text-ink mt-1">
                    {r.review_text}
                  </p>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

function RateClientCard({
  vacancyId,
  myRating,
  canRate,
}: {
  vacancyId: number;
  myRating: number | null;
  canRate: boolean;
}) {
  const [rating, setRating] = useState<number>(myRating ?? 0);
  const [text, setText] = useState<string>("");
  const submit = useSubmitReview(vacancyId);
  const del = useDeleteReview(vacancyId);
  const navigate = useNavigate();

  // Один відгук на клієнта (юзер 2026-06-11): якщо вже залишив — показуємо
  // його + кнопку видалити. Новий можна лише після видалення старого.
  const handleDelete = async () => {
    const ok = await new Promise<boolean>((res) => {
      const wa = tg() as unknown as {
        showConfirm?: (msg: string, cb: (c: boolean) => void) => void;
      } | null;
      if (wa?.showConfirm) {
        wa.showConfirm("Видалити свій відгук про цього клієнта?", (c) =>
          res(!!c),
        );
      } else {
        res(window.confirm("Видалити свій відгук про цього клієнта?"));
      }
    });
    if (!ok) return;
    try {
      await del.mutateAsync();
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      setRating(0);
      setText("");
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert((e as Error).message);
    }
  };

  // Низька оцінка (1-3) МАЄ бути аргументована текстом (юзер 2026-06-09).
  const needsComment = rating >= 1 && rating <= 3 && !text.trim();
  const commentRef = useRef<HTMLTextAreaElement | null>(null);

  const handleSubmit = async () => {
    if (rating < 1) {
      showAppAlert("Постав від 1 до 5 зірок");
      return;
    }
    if (rating <= 3 && !text.trim()) {
      showAppAlert(
        "Для оцінки 1-3 зірки додай коментар — поясни, що не так.",
      );
      return;
    }
    try {
      const res = await submit.mutateAsync({
        rating,
        review_text: text.trim() || undefined,
      });
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      showReviewSaved(res, () => navigate("/quests"));
      setText("");
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert((e as Error).message);
    }
  };

  if (!canRate) {
    return (
      <div className="rounded-ios-card bg-surface shadow-card-soft p-4">
        <div className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-ink-dim mb-1.5">
          Оцінка клієнта
        </div>
        <div className="text-[0.8125rem] text-ink-dim leading-snug">
          Оцінювати можуть юзери які відкривали контакти або PRO-підписники
          — щоб відгуки залишали ті хто реально працював.
        </div>
      </div>
    );
  }

  // Вже залишив відгук → показуємо його + видалення (без форми).
  if (myRating !== null) {
    return (
      <div className="rounded-ios-card bg-surface shadow-card p-4">
        <div className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-ink-dim mb-2">
          Твоя оцінка
        </div>
        <div className="flex items-center gap-2 mb-2">
          <StarRow value={myRating} />
        </div>
        <div className="text-[0.75rem] text-ink-dim leading-snug mb-3">
          Ти вже залишив відгук про цього клієнта. Один відгук на клієнта —
          щоб написати новий, спершу видали поточний.
        </div>
        <button
          type="button"
          onClick={handleDelete}
          disabled={del.isPending}
          className={
            "block w-full rounded-full py-3 font-display text-[0.8125rem] font-semibold transition " +
            (del.isPending
              ? "bg-ink/10 text-ink-dim cursor-not-allowed"
              : "bg-danger/10 text-danger active:bg-danger/20")
          }
        >
          {del.isPending ? "Видаляю..." : "Видалити відгук"}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-ios-card bg-surface shadow-card p-4">
      <div className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-ink-dim mb-2">
        Твоя оцінка
      </div>
      <div className="flex items-center justify-between mb-2">
        <StarRow
          value={rating}
          onPick={(n) => {
            setRating(n);
            // Для 1-3 зірок коментар обовʼязковий — одразу ставимо курсор у
            // поле, щоб людина не гадала, чому кнопка сіра.
            if (n <= 3) setTimeout(() => commentRef.current?.focus(), 60);
          }}
        />
      </div>
      {rating >= 1 && rating <= 3 && (
        <div className="mb-3 rounded-xl bg-primary/[0.08] px-3 py-2 text-[0.75rem] leading-snug text-primary [text-wrap:balance]">
          Для оцінки {rating} {rating === 1 ? "зірка" : "зірки"} потрібен
          коментар — напиши коротко, що пішло не так.
        </div>
      )}
      <textarea
        ref={commentRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder={
          rating >= 1 && rating <= 3
            ? "Поясни оцінку — що пішло не так (обовʼязково)"
            : "Кілька слів про клієнта (опційно)"
        }
        /* Поле мусить читатись як поле (owner 2026-09-17: «блок, де вводити
           відгук, такого кольору, що не видно, що там щось є»): рамка-
           волосинка + фон темніший за картку. */
        className="w-full resize-none rounded-xl border border-ink/[0.12] bg-bg px-3 py-2.5 text-[0.8125rem] leading-relaxed text-ink placeholder:text-ink-faint focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submit.isPending || rating < 1 || needsComment}
        className={
          "mt-3 block w-full rounded-full py-3 font-display text-[0.8125rem] font-semibold transition " +
          (submit.isPending || rating < 1 || needsComment
            ? "bg-ink/10 text-ink-dim cursor-not-allowed"
            : "bg-primary text-white active:bg-primary-deep")
        }
      >
        {submit.isPending
          ? "Зберігаю..."
          : needsComment
            ? "Спершу напиши коментар"
            : myRating
              ? "Оновити відгук"
              : "Залишити відгук"}
      </button>
      <p className="mt-2 text-center text-[0.75rem] leading-snug text-ink-dim [text-wrap:balance]">
        За кожну оцінку нараховуємо 1 ₴ на бонусний рахунок
      </p>
    </div>
  );
}

export function FeedVacancyPage() {
  const { id } = useParams<{ id: string }>();
  const vacancyId = Number(id);
  const q = useFeedVacancy(vacancyId);
  const unlock = useUnlockContacts(vacancyId);
  const toggleSave = useToggleSaveVacancy(vacancyId);
  // Збереження доступне з підпискою, але кнопку бачать усі — безкоштовним
  // вона сіра й під замком (owner 2026-09-12).
  const subQ = useMySubscription();
  const isPaid = subQ.data ? isActiveSubscription(subQ.data) : false;
  // Клік по замкненій кнопці «Зберегти» відкриває той самий попап, що й
  // замкнені рядки розділів (owner 2026-09-12).
  const [savedLocked, setSavedLocked] = useState(false);
  const notVac = useFlagNotVacancy(vacancyId);
  const hideVac = useHideVacancy(vacancyId);
  // Owner 2026-09-15: «на окремій сторінці вакансії нема адмінської кнопки
  // видалити» — у стрічці вона була, а тут ні.
  const adminRemove = useAdminRemoveVacancy(vacancyId);
  const meQ = useMe();
  const navigate = useNavigate();
  // Попап про тижневий ліміт контактів (owner 2026-09-08): відповідаємо там,
  // де сталась дія. Лист у чат лишається за ботом.
  const limitPopup = useContactLimitPopup();

  // «Видалити вакансію» — прибрати ОСОБИСТО в себе (owner 2026-08-15).
  // Без підтвердження: дія тиха і на інших не впливає. Після видалення
  // повертаємо в стрічку — на сторінці вже нічого дивитись.
  const handleAdminRemove = () => {
    if (adminRemove.isPending) return;
    tg()?.HapticFeedback?.impactOccurred?.("medium");
    showAppAlert(
      "Вакансія зникне зі стрічки у всіх і відкриватиметься як знята, без контактів. Повідомлення в ботах видаляться, якщо їм менше кількох годин. Дію не відкотити.",
      {
        title: "Видалити вакансію?",
        emoji: "❌",
        buttons: [
          {
            label: "Видалити",
            variant: "danger",
            onClick: () =>
              adminRemove.mutate(undefined, {
                onSuccess: () => {
                  tg()?.HapticFeedback?.notificationOccurred?.("success");
                  showToast("Вакансію знято", { emoji: "✓" });
                  navigate("/feed", { replace: true });
                },
                onError: (e) =>
                  showAppAlert((e as Error).message, {
                    title: "Не вдалося видалити",
                  }),
              }),
          },
          { label: "Скасувати", variant: "ghost" },
        ],
      },
    );
  };

  const handleDeleteVacancy = () => {
    if (hideVac.isPending) return;
    tg()?.HapticFeedback?.impactOccurred?.("light");
    hideVac.mutate(undefined, {
      onSuccess: () => {
        tg()?.HapticFeedback?.notificationOccurred?.("success");
        showToast("Прибрали 🌤", { emoji: "🗑" });
        navigate("/feed");
      },
      onError: (e) => showAppAlert((e as Error).message, { title: "Помилка" }),
    });
  };

  const handleNotVacancy = async () => {
    if (notVac.isPending) return;
    tg()?.HapticFeedback?.impactOccurred?.("light");
    showAppAlert("Це не вакансія / спам? Прибрати її зі стрічки?", {
      title: "Це не вакансія",
      emoji: "❌",
      buttons: [
        {
          label: "Так, прибрати",
          variant: "danger",
          onClick: () =>
            notVac.mutate(undefined, {
              onSuccess: () => {
                tg()?.HapticFeedback?.notificationOccurred?.("success");
                showToast("Прибрали цю вакансію", { emoji: "✓" });
                navigate("/feed");
              },
              onError: (e) =>
                showAppAlert((e as Error).message, { title: "Помилка" }),
            }),
        },
        { label: "Скасувати", variant: "ghost" },
      ],
    });
  };

  // Відкрив сторінку вакансії (у т.ч. з бот-сповіщення) → позначаємо
  // «переглянуто» для бейджа/затемнення у стрічці (юзер 2026-06-11).
  useEffect(() => {
    if (!Number.isNaN(vacancyId)) markVacancyViewed(vacancyId);
  }, [vacancyId]);

  if (!id || Number.isNaN(vacancyId)) {
    return (
      <div className="min-h-screen p-6">
        <BackNav />
        <CenteredError message="Невалідний ID вакансії" />
      </div>
    );
  }
  if (q.isLoading) {
    return (
      <div className="min-h-screen p-6">
        <BackNav />
        <Spinner />
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="min-h-screen p-6">
        <BackNav />
        <CenteredError message={q.error.message} />
      </div>
    );
  }
  if (!q.data) {
    return (
      <div className="min-h-screen p-6">
        <BackNav />
        <CenteredError message="Вакансію не знайдено" />
      </div>
    );
  }
  const v = q.data;

  // Знята вакансія (owner 2026-09-25): ні тексту, ні контактів, ні кнопок —
  // лише пояснення. Сервер у цьому стані їх і не віддає.
  if (v.removed) {
    return (
      <div className="min-h-screen pb-16 overflow-x-hidden">
        <div className="px-5 pt-5">
          <BackNav onBack={() => navigate("/feed")} backLabel="Стрічка" showMenu />
        </div>
        <div className="px-5">
          <RemovedVacancyNotice />
        </div>
      </div>
    );
  }

  // Threads/Instagram URLs з footer'а (вшиті в <a href> анкори «тиць»).
  // Бекенд parse_contacts вже витягує їх у contacts.urls, але для старих
  // вакансій (до commit 1b681ee) дозабиваємо їх з тексту як fallback —
  // юзер хоче бачити Threads/Instagram links у блоку Contacts.
  const mergedContacts = (() => {
    const footer = extractFooter(v.text);
    const extra: string[] = [];
    if (footer.threadsUrl && !v.contacts.urls.includes(footer.threadsUrl))
      extra.push(footer.threadsUrl);
    if (footer.instagramUrl && !v.contacts.urls.includes(footer.instagramUrl))
      extra.push(footer.instagramUrl);
    // Фільтр застосовуємо ЗАВЖДИ, а не лише коли є extra: інакше інвайт у
    // канал лишався на сторінці вакансії (owner 2026-08-31, вдруге).
    return {
      ...v.contacts,
      urls: filterContactLinks([...v.contacts.urls, ...extra]),
    };
  })();

  // Кого саме оцінюємо. Бекенд віддає це готовою парою: у Telegram — юзернейм
  // замовника, у Threads — нік автора поста (owner 2026-08-27). Випадковий
  // @username із тексту в цю роль не потрапляє: fallback лишаємо тільки для
  // telegram_chat, де його витяг структурний парсер.
  const effectiveContactUsername =
    v.customer_handle ??
    v.contact_username ??
    (v.source === "telegram_chat" ? mergedContacts.telegrams[0] ?? null : null);
  const canRate = effectiveContactUsername !== null && v.contacts_unlocked;

  return (
    <div className="min-h-screen pb-16 overflow-x-hidden">
      {limitPopup.node}
      <div className="px-5 pt-5">
        {/* Юзер 2026-06-12: «Стрічка» → стрічка вакансій. Юзер 2026-06-25:
            додано «Меню» справа (Стрічка зліва, Меню справа). */}
        <BackNav onBack={() => navigate("/feed")} backLabel="Стрічка" showMenu />
      </div>

      {/* Hero */}
      <div className="px-5 pt-3">
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          <WorkKindBadge kind={v.work_kind} />
          {WORK_FORMAT_BADGE[v.work_format] && (
            <span
              className={
                "inline-block font-mono text-[0.625rem] tracking-[0.04em] px-2 py-0.5 rounded-full " +
                WORK_FORMAT_BADGE[v.work_format].cls
              }
            >
              {WORK_FORMAT_BADGE[v.work_format].label}
            </span>
          )}
          {/* Вакансія може належати кільком сферам (owner 2026-08-28) —
              показуємо всі, головну першою. */}
          {(v.category_names?.length
            ? v.category_names
            : v.category_name
              ? [v.category_name]
              : []
          ).map((name) => (
            <span
              key={name}
              className="inline-block font-mono text-[0.625rem] tracking-[0.02em] text-primary bg-primary/10 px-2 py-0.5 rounded-full truncate min-w-0"
            >
              {name}
            </span>
          ))}
          {SOURCE_LABEL[v.source] && (
            <span className="inline-block font-mono text-[0.625rem] tracking-[0.04em] text-ink-dim bg-ink/5 px-2 py-0.5 rounded-full">
              {SOURCE_LABEL[v.source]}
            </span>
          )}
          <span className="font-mono text-[0.625rem] text-ink-dim">
            {relTime(v.captured_at)}
          </span>
          <FactPills pay={v.pay} experience={v.experience} location={v.location} />
        </div>
        {/* Summary НЕ показуємо на детальній сторінці — тільки повний
            оригінальний текст нижче (юзер 2026-06-10: «два тексти заважають»).
            Summary лишається лише в бот-сповіщеннях. */}
        {v.is_blacklisted && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[0.75rem] text-red-700">
            ⚠ Цей клієнт у чорному списку. Будь обережним.
          </div>
        )}
      </div>

      {/* Full text */}
      <div className="px-5 pt-2">
        <div className="rounded-ios-card bg-surface shadow-card p-4">
          <div className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-ink-dim mb-2">
            Текст вакансії
          </div>
          <div
            className="text-[0.875rem] leading-relaxed text-ink whitespace-pre-wrap break-words [overflow-wrap:anywhere] [&_b]:font-semibold [&_strong]:font-semibold [&_u]:underline [&_i]:italic [&_em]:italic [&_a]:text-primary [&_a]:underline"
            onClick={openExternalOnAnchorClick}
            dangerouslySetInnerHTML={{
              // Текст із посиланнями всередині, якщо парсер його прислав
              // (owner 2026-08-31).
              __html: renderVacancyHtml(
                v.text_html || v.text,
                v.contacts_unlocked,
              ),
            }}
          />
        </div>
      </div>

      {/* Додаткові матеріали — файли замовника, між текстом і контактами
          (owner 2026-09-12). */}
      <VacancyMediaBlock media={v.media ?? []} />

      {/* Contacts — redesigned 2026-05-23 (юзер: «контакти беремо з
          footer'а парсера, не з тіла; для telegram_channel немає footer'а
          → не показуємо список, тільки CTA розблокувати вакансію»). */}
      {(() => {
        // Посилання на пост у групі — контакт, коли іншого немає (owner
        // 2026-09-24). Сервер віддає його лише для чатів без ніка/телефону/пошти.
        const postUrl = v.post_url ?? null;
        const totalContacts =
          mergedContacts.telegrams.length +
          mergedContacts.phones.length +
          mergedContacts.emails.length +
          mergedContacts.urls.length +
          (postUrl ? 1 : 0);
        const isChannelSource = v.source === "telegram_channel";

        // Для TG-каналів без розпарсених контактів — показуємо альтернативу:
        // «контакти в самому тексті вакансії» + unlock CTA.
        if (isChannelSource && totalContacts === 0) {
          return (
            <div className="px-5 pt-3">
              <div className="rounded-ios-card bg-surface shadow-card p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  <span className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-ink-dim">
                    Контакти у тексті
                  </span>
                </div>
                {v.contacts_unlocked ? (
                  <div className="text-[0.78125rem] text-ink-dim leading-snug">
                    Контакти ↑ у тексті вакансії вище (телефони / @username /
                    email). Перечитай уважно.
                  </div>
                ) : (
                  <BlurredContacts
                    attemptsLeft={v.attempts_left_today}
                    onUnlock={() =>
                      unlock.mutate(undefined, {
                        onSuccess: (res) => limitPopup.check(res),
                      })
                    }
                    onShowLimit={() => navigate("/subscription")}
                    isUnlocking={unlock.isPending}
                  />
                )}
              </div>
            </div>
          );
        }

        return (
          <div className="px-5 pt-3">
            <div className="rounded-ios-card bg-surface shadow-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-ink-dim">
                  Контакти замовника
                </span>
                {v.contacts_unlocked && totalContacts > 0 && (
                  <span className="ml-auto font-mono text-[0.625rem] text-ink-faint">
                    {totalContacts} шт.
                  </span>
                )}
              </div>
              {totalContacts === 0 ? (
                <div className="rounded-xl border border-dashed border-ink/15 py-5 px-4 text-center">
                  <div className="text-[1.25rem] mb-1">🔍</div>
                  <div className="text-[0.75rem] text-ink-dim leading-snug">
                    У цій вакансії не вказано прямих контактів.
                  </div>
                </div>
              ) : v.contacts_unlocked ? (
                <ContactsList contacts={mergedContacts} postUrl={postUrl} />
              ) : (
                <BlurredContacts
                  attemptsLeft={v.attempts_left_today}
                  onUnlock={() =>
                      unlock.mutate(undefined, {
                        onSuccess: (res) => limitPopup.check(res),
                      })
                    }
                  onShowLimit={() => navigate("/subscription")}
                  isUnlocking={unlock.isPending}
                />
              )}
            </div>
          </div>
        );
      })()}

      {/* AI-помічник для відгуку — як в inline-розгортанні ленти (юзер
          2026-06-11: на окремій сторінці вакансії кнопки генерації не було). */}
      <div className="px-5 pt-3">
        <AIHelperStub vacancyId={vacancyId} contactUsername={effectiveContactUsername} />
      </div>

      {/* «Це не вакансія» — скарга (юзер 2026-06-25). Розміщено одразу під
          AI-помічником, щоб було видно без прокрутки до самого низу.
          1 скарга ховає в цього юзера; 2 різних → глобально з бази. */}
      {/* «Зберегти» — окремим рядом над скаргою і видаленням, як у боті
          (owner 2026-09-06): дія іншого роду, ніж прибрати вакансію.
          Owner 2026-09-12: безкоштовним кнопку НЕ ховаємо — вона сіра й під
          замком, як рядки розділів на Головній. Правило на всю аппку: для
          безкоштовних нічого не прибираємо і не перемальовуємо, закрите просто
          сіре із замком. */}
      <div className="px-5 pt-3">
        <button
          type="button"
          onClick={() => {
            tg()?.HapticFeedback?.impactOccurred?.("light");
            if (!isPaid) {
              setSavedLocked(true);
              return;
            }
            toggleSave.mutate(undefined, {
              onSuccess: (r) =>
                showToast(r.saved ? "Збережено" : "Прибрали зі збережених", {
                  emoji: r.saved ? "🔖" : "✓",
                }),
              onError: (e) =>
                showAppAlert((e as Error).message, { title: "Помилка" }),
            });
          }}
          disabled={toggleSave.isPending}
          className={
            "flex w-full items-center justify-center gap-2 rounded-full py-2.5 " +
            "font-display text-[0.78125rem] font-semibold transition " +
            "active:scale-[0.98] disabled:opacity-50 " +
            (!isPaid
              ? "bg-ink/[0.04] border border-ink/[0.08] text-ink-faint"
              : v.is_saved
                ? "bg-primary/10 border border-primary/30 text-primary"
                : "bg-surface-soft border border-ink/[0.08] text-ink-dim")
          }
        >
          <Icon
            name={v.is_saved && isPaid ? "bookmark-fill" : "bookmark"}
            size={16}
            color={!isPaid ? "#A8A29C" : undefined}
          />
          {v.is_saved && isPaid ? "Збережено" : "Зберегти"}
          {!isPaid && <Icon name="lock" size={14} color="#A8A29C" />}
        </button>
      </div>

      <div className="px-5 pt-3 flex gap-2">
        <button
          type="button"
          onClick={handleNotVacancy}
          disabled={notVac.isPending}
          className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-surface-soft border border-danger/20 px-4 py-2.5 font-display text-[0.78125rem] font-semibold text-danger whitespace-nowrap active:scale-[0.98] transition disabled:opacity-50"
        >
          <Icon name="flag" size={16} />
          Не вакансія
        </button>
        <button
          type="button"
          onClick={handleDeleteVacancy}
          disabled={hideVac.isPending}
          className="flex flex-1 items-center justify-center gap-2 rounded-full bg-surface-soft border border-ink/[0.08] py-2.5 font-display text-[0.78125rem] font-semibold text-ink-dim whitespace-nowrap active:scale-[0.98] transition disabled:opacity-50"
        >
          <Icon name="trash" size={16} />
          Видалити
        </button>
      </div>

      {/* АДМІН: зняти вакансію у всіх — той самий виклик, що у стрічці. */}
      {meQ.data?.is_admin && (
        <div className="px-5 pt-2">
          <button
            type="button"
            onClick={handleAdminRemove}
            disabled={adminRemove.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-danger/30 bg-danger/10 py-2.5 font-display text-[0.78125rem] font-semibold text-danger transition active:scale-[0.98] disabled:opacity-50"
          >
            <Icon name="trash" size={16} />
            {adminRemove.isPending ? "Видаляю…" : "Видалити вакансію (адмін)"}
          </button>
        </div>
      )}

      {/* Reviews about client — \u0445\u043e\u0447 \u0454 username (\u0440\u0435\u0439\u0442\u0438\u043d\u0433 \u0431\u0430\u0437\u0443\u0454\u0442\u044c\u0441\u044f \u043d\u0430 @) */}
      {effectiveContactUsername && (
        <div className="px-5 pt-3">
          <ReviewsSection
            vacancyId={vacancyId}
            contactUsername={effectiveContactUsername}
            unlocked={v.contacts_unlocked}
            clientRatingAvg={v.client_rating_avg}
            clientRatingCount={v.client_rating_count}
            clientReviewsCount={v.client_reviews_count}
          />
        </div>
      )}

      {/* Rate the client */}
      {effectiveContactUsername && (
        <div className="px-5 pt-3">
          <RateClientCard
            vacancyId={vacancyId}
            myRating={v.my_rating}
            canRate={canRate}
          />
        </div>
      )}

      <LockedSectionPopup
        sectionKey={savedLocked ? "saved" : null}
        open={savedLocked}
        onClose={() => setSavedLocked(false)}
      />

    </div>
  );
}
